import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Constants from "common/Constants";
import { getTodayDate, gethhmmss } from "utils/DateUtil";
import { callPost } from "utils/CommonApi";
import ExamPlanFinalCard from "./ExamPlanFinalCard";
import ExamPlanFinalDetail from "./ExamPlanFinalDetail";
import ExamPlanGuide from "./ExamPlanGuide";
import ExamPlanHeader from "./ExamPlanHeader";
import ExamPlanMissionStatus from "./ExamPlanMissionStatus";
import ExamPlanTabs from "./ExamPlanTabs";
import { EXAM_PLAN_GUIDE_ITEMS, EXAM_PLAN_REVIEWS } from "./examPlanContentData";
import ExamPlanReviews from "./ExamPlanReviews";
import { useHistory, useLocation } from "react-router-dom";
import usexAPI from "store/customHooks/usexAPI";
import { xAPI_ID_VALUE } from "utils/xAPI_PATH";
import { EXAM_PLAN_XAPI_CONTENT } from "./examPlanXapi";
import "./ExamPlan.scss";

const loadExamPlanScore = () => import(/* webpackChunkName: "exam-plan-score" */ "./ExamPlanScore");
const ExamPlanScore = lazy(loadExamPlanScore);
const getExpandedStorageKey = (planSeq) => `examPlanExpanded_${planSeq || "default"}`;
const getSelectedTabStorageKey = (planSeq) => `examPlanSelectedTab_${planSeq || "default"}`;
/**
 * 미션 안내를 한 번 본 뒤에는 미션 현황이 먼저 열려야 한다.
 * 서버가 FIRST_ACCESS_DT 를 내려주지 않아 로컬에 확인 여부를 기록한다.
 */
const getGuideViewedStorageKey = (planSeq) => `examPlanGuideViewed_${planSeq || "default"}`;
const isGuideViewed = (planSeq) => localStorage.getItem(getGuideViewedStorageKey(planSeq)) === "Y";
const markGuideViewed = (planSeq) => localStorage.setItem(getGuideViewedStorageKey(planSeq), "Y");

/**
 * 영역을 열 때 보여줄 탭 : 안내를 봤으면 미션 현황, 아니면 미션 안내
 * 판정만 하고 '봤음' 기록은 실제로 안내 탭이 열린 뒤에 남긴다.
 * (판정 시점에 기록하면 최초 진입에서도 곧바로 미션 현황이 열려버린다)
 */
const resolveOpenTab = (planSeq, firstAccessDt) =>
    localStorage.getItem(getSelectedTabStorageKey(planSeq)) ||
    (firstAccessDt || isGuideViewed(planSeq) ? "mission" : "guide");
const getInitialExpanded = (examPlanInfo) => {
    const storedExpanded = localStorage.getItem(getExpandedStorageKey(examPlanInfo?.PLAN_SEQ));

    if (storedExpanded != null) return storedExpanded === "Y";
    return examPlanInfo?.JOIN_YN === "Y" && !examPlanInfo?.FIRST_ACCESS_DT;
};

function formatGuideDate(value) {
    if (!value) return "";

    const date = typeof value === "number" ? moment(value) : moment(value, ["YYYYMMDD", "YYYY-MM-DD"], true);
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
}

function ExamPlan({ userId, mmbrSeq, examPlanInfo, isSetupPending, openRequest, onRequestSetup, isHome }) {
    const history = useHistory();
    /**
     * 패널이 App 전역에 상주하면서 페이지를 옮겨도 언마운트되지 않는다.
     * 미션을 수행하고 돌아왔을 때 진행률이 갱신되도록 페이지 이동마다 미션현황을 다시 조회한다.
     */
    const { pathname, search } = useLocation();
    const { ServiceUnit_B } = usexAPI();
    const [isExpanded, setIsExpanded] = useState(() => isHome && getInitialExpanded(examPlanInfo));
    /**
     * 열림/닫힘 상태는 홈에서 여닫은 것만 기억한다.
     * 패널이 전역으로 뜨면서, 다른 화면에서 연 상태가 계속 남아 페이지를 덮는 문제가 있었다.
     * 홈 밖에서는 항상 닫힌 채로 시작하고 저장도 하지 않는다.
     */
    const persistExpanded = useCallback(
        (expanded) => {
            if (!isHome) return;

            localStorage.setItem(getExpandedStorageKey(examPlanInfo?.PLAN_SEQ), expanded ? "Y" : "N");
        },
        [examPlanInfo, isHome],
    );
    const [tab, setTab] = useState(() => resolveOpenTab(examPlanInfo?.PLAN_SEQ, examPlanInfo?.FIRST_ACCESS_DT));
    const [missionView, setMissionView] = useState("list");
    const [guideMission, setGuideMission] = useState(null);
    const [missionStatusData, setMissionStatusData] = useState(null);
    const missionStatusTotal = missionStatusData?.MIS_STATUS_TOTAL;
    const missionPlanInfo = missionStatusData?.MIS_PLAN_INFO || examPlanInfo;
    const missionEndDt = missionStatusTotal?.END_DT;
    /**
     * AI독서실 운영 여부
     * 미션현황 응답의 BRCS_NEXT_INFO 와 AI독서실 운영 정보(BRCS_INFO) 를 함께 확인한다.
     * (BRCS_NEXT_INFO 는 다음 회차 정보라 CMS 에 오늘 일정이 있어도 비어 있을 수 있다)
     */
    const [brcsTodayInfo, setBrcsTodayInfo] = useState(null);
    const isAiStudyRoomActive = useMemo(() => {
        // 오늘 날짜 + 운영 시간에 들어와 있으면 활성
        const isOperating = (info) => {
            if (!info?.BRCS_DT || !info?.STD_START_TM || !info?.STD_END_TM) return false;

            const nowTm = parseInt(gethhmmss(), 10);
            const startTm = parseInt(info.STD_START_TM, 10);
            const endTm = parseInt(info.STD_END_TM, 10);

            return String(info.BRCS_DT).replace(/-/g, "") === getTodayDate() && startTm <= nowTm && nowTm < endTm;
        };

        return isOperating(missionStatusData?.BRCS_NEXT_INFO) || isOperating(brcsTodayInfo);
    }, [brcsTodayInfo, missionStatusData]);
    const totalMissionCount = missionStatusTotal ? Math.max(0, missionStatusTotal.TOTAL_MISSION_CNT) : 7;
    // 시험기간 등록 미션은 서버가 COMPLETE_MISSION_CNT 에 이미 포함해서 내려줌
    const completeMissionCount = Math.min(
        totalMissionCount,
        Math.max(0, missionStatusTotal?.COMPLETE_MISSION_CNT || 0),
    );
    /**
     * 시험대비모드 기간 종료 여부.
     * 종료 후에도 일주일간 화면은 열어두되 미션 진입(GO)은 막는다.
     */
    const isMissionExpired = useMemo(() => {
        const endDDay = Number(examPlanInfo?.END_D_DAY);

        if (Number.isFinite(endDDay)) return endDDay < 0;

        const missionEndDate = moment(missionEndDt, ["YYYYMMDD", "YYYY-MM-DD"], true);
        return missionEndDate.isValid() ? moment().isAfter(missionEndDate.endOf("day")) : false;
    }, [examPlanInfo, missionEndDt]);
    const planSeq = examPlanInfo?.PLAN_SEQ;
    const firstAccessDt = examPlanInfo?.FIRST_ACCESS_DT;
    const examPlanJoined = examPlanInfo?.JOIN_YN === "Y";
    const onEditScore = useCallback(() => {
        ServiceUnit_B(xAPI_ID_VALUE.serviceUnit.btn_epm_0015).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
        history.push("/mypage/myExamInfo");
    }, [ServiceUnit_B, history]);
    const onMissionVisit = useCallback(
        (menuCode, upMenuCode) => {
            if (!menuCode) return;

            callPost(
                { user_id: userId },
                {
                    REQUEST_API: Constants.MID_STUDY_API.DAILY_VISIT_MISSION,
                    HTTP_METHOD: Constants.HTTP_METHOD.POST,
                    ...(upMenuCode ? { BODY: JSON.stringify({ UP_MENU_CD: upMenuCode }, null, 2) } : {}),
                },
                null,
                { MMBR_SEQ: mmbrSeq, MENU_CD: menuCode },
            );
        },
        [mmbrSeq, userId],
    );
    const onMissionMove = useCallback(
        (route, menuCode, upMenuCode) => {
            /**
             * 이미 그 화면에 있으면 다시 push 하지 않는다.
             * 같은 URL 을 또 쌓으면 화면이 재렌더링되면서 상태가 어긋나고
             * (시험특강에서 Go 를 다시 누르면 강좌 목록이 로딩 상태로 굳었다)
             * 뒤로가기 때도 같은 화면이 반복된다.
             * 방문 기록은 미션 판정에 필요하므로 push 여부와 상관없이 남긴다.
             */
            if (route !== `${pathname}${search}`) history.push(route);

            if (menuCode && upMenuCode) onMissionVisit(menuCode, upMenuCode);
        },
        [history, onMissionVisit, pathname, search],
    );

    /**
     * 시험기간은 입력돼 있으면 그대로 노출한다. (일정 미노출 플래그와 무관)
     * 계획 정보(PREV_MISS_PLAN_INFO)에 기간이 비어 있어도 미션현황 응답(MIS_PLAN_INFO)에는
     * 남아 있는 경우가 있어, 둘 중 값이 있는 쪽을 쓴다.
     */
    const exam = useMemo(() => {
        if (!examPlanInfo) return {};

        const parseExamDate = (value) => moment(value, ["YYYYMMDD", "YYYY-MM-DD"], true);
        const startDate = parseExamDate(examPlanInfo.EXAM_START_DT || missionPlanInfo?.EXAM_START_DT);
        const endDate = parseExamDate(examPlanInfo.EXAM_END_DT || missionPlanInfo?.EXAM_END_DT);
        const hasExamPeriod = startDate.isValid() && endDate.isValid();
        return {
            gradeSemester: [examPlanInfo.GRAD_NM, examPlanInfo.SMST_NM].filter(Boolean).join(" "),
            name: examPlanInfo.EXAM_DVSN_CD_NM || "",
            hasPeriod: hasExamPeriod,
            period: hasExamPeriod ? `${startDate.format("YYYY.MM.DD")} ~ ${endDate.format("MM.DD")}` : "",
            fullPeriod: hasExamPeriod ? `${startDate.format("YYYY.MM.DD")} ~ ${endDate.format("YYYY.MM.DD")}` : "",
            examStart: hasExamPeriod ? startDate.clone().startOf("day") : null,
            /**
             * 카운트다운은 학생이 등록/수정한 시험기간(EXAM_START_DT ~ EXAM_END_DT)만 따른다.
             * 이전에는 미션 종료일(MIS_STATUS_TOTAL.END_DT)을 우선 사용해서
             * 시험기간을 수정해도 남은 시간이 그대로였다.
             */
            examEnd: endDate.isValid() ? endDate.clone().endOf("day") : null,
        };
    }, [examPlanInfo, missionPlanInfo]);
    /** 미션 안내 문구의 횟수는 서버 미션 정보(MIS_STATUS_LIST)를 따른다. */
    const missionCountBySortOrd = useMemo(() => {
        const missionList = missionStatusData?.MIS_STATUS_LIST;

        if (!Array.isArray(missionList)) return {};

        return missionList.reduce((acc, mission) => {
            if (mission?.SORT_ORD != null && mission?.MISSION_COUNT) acc[mission.SORT_ORD] = mission.MISSION_COUNT;

            return acc;
        }, {});
    }, [missionStatusData]);

    const guideItems = useMemo(() => {
        // 시험 정보가 없어도 {count} 자리표시자는 기본값으로 채워야 해서 항상 변환한다.
        const scoreDeadlineDate = formatGuideDate(missionPlanInfo?.SC_DEADLINE_DT);
        const rewardDate = formatGuideDate(missionPlanInfo?.REWARD_DT);
        const examModeStartDate = formatGuideDate(missionStatusTotal?.START_DT);
        const examModeEndDate = formatGuideDate(missionStatusTotal?.END_DT);
        const replaceExamInfo = (text) =>
            text
                .replace("#학기", missionPlanInfo?.SMST_NM || "#학기")
                .replace("##고사", missionPlanInfo?.EXAM_DVSN_CD_NM || "##고사");

        return EXAM_PLAN_GUIDE_ITEMS.map((guide) => {
            const missionCount = missionCountBySortOrd[guide.mission];
            const hasExamInfo = [0, 1, 4, 7, 8].includes(guide.mission);
            const hasCountToken = guide.defaultCount != null;

            if (!missionCount && !hasExamInfo && !hasCountToken) return guide;

            /**
             * 안내 문구의 횟수는 {count} 자리표시자로만 적어두고 서버 미션 횟수로 채운다.
             * (예전에는 문구의 모든 "숫자+강/회/개"를 바꾸는 방식이라
             *  "1회 인정" 같이 미션 횟수와 무관한 문구까지 함께 바뀌었고,
             *  "3번"처럼 단위가 다른 문구는 아예 반영되지 않았다.)
             */
            const resolvedCount = missionCount || guide.defaultCount;
            const replaceCount = (text) => (resolvedCount != null ? text.replace(/\{count\}/g, resolvedCount) : text);
            const replaceText = (text) => (hasExamInfo ? replaceCount(replaceExamInfo(text)) : replaceCount(text));

            return {
                ...guide,
                title: replaceCount(guide.title),
                subtitle: replaceText(guide.subtitle),
                sections: guide.sections.map((section, sectionIndex) => ({
                    ...section,
                    title: section.title ? replaceText(section.title) : section.title,
                    items: section.items.map((item) => {
                        const originalText = typeof item === "string" ? item : item?.text;

                        if (typeof originalText !== "string") return item;

                        let text = replaceText(originalText);

                        if (item?.dateType === "examModePeriod") {
                            // 학생이 입력한 시험기간이 아니라 서버 시험대비모드 기간을 쓴다.
                            if (examModeStartDate) text = text.replace("YYYY-MM-dd", examModeStartDate);
                            if (examModeEndDate) text = text.replace("YYYY-MM-dd", examModeEndDate);
                        } else if (guide.mission === 0 && sectionIndex === 1 && scoreDeadlineDate) {
                            text = text.replace("YYYY-MM-dd", scoreDeadlineDate);
                        } else if (guide.mission === 0 && sectionIndex === 2 && rewardDate) {
                            text = text.replace("YYYY-MM-dd", rewardDate);
                        }

                        return typeof item === "string" ? text : { ...item, text };
                    }),
                })),
            };
        });
    }, [missionPlanInfo, missionStatusTotal, missionCountBySortOrd]);

    /**
     * 시험대비모드에 막 참여한 경우(안내 팝업 저장 직후)에는 미션 안내부터 다시 보여준다.
     * 서버가 FIRST_ACCESS_DT 를 내려주지 않아 로컬 기록에만 의존하는데,
     * 재참여 전 기록이 남아 있으면 최초 진입인데도 미션 현황이 열려버린다.
     */
    const wasSetupPendingRef = useRef(isSetupPending);

    useEffect(() => {
        if (wasSetupPendingRef.current && !isSetupPending) {
            localStorage.removeItem(getGuideViewedStorageKey(planSeq));
            localStorage.setItem(getSelectedTabStorageKey(planSeq), "guide");
            setTab("guide");
            setMissionView("list");
            setGuideMission(null);
        }

        wasSetupPendingRef.current = isSetupPending;
    }, [isSetupPending, planSeq]);

    /**
     * 홈에 들어오면 저장해둔 상태로 되돌리고, 홈이 아닌 화면으로 이동하면 닫는다.
     * (닫는 동작은 저장하지 않으므로 홈으로 돌아오면 열어둔 상태가 그대로 살아난다)
     */
    useEffect(() => {
        setIsExpanded(isHome ? getInitialExpanded(examPlanInfo) : false);
    }, [examPlanInfo, isHome, pathname]);

    /**
     * 홈 카드/안내 팝업에서 "열어달라"고 요청한 순간에만 펼친다.
     * 마운트 시점의 값까지 반응하면, 다른 화면으로 이동해 패널이 잠시 사라졌다가
     * 다시 렌더될 때 예전 요청이 되살아나 상세 페이지에서 강제로 펼쳐진다.
     */
    const handledOpenRequestRef = useRef(openRequest);
    useEffect(() => {
        if (openRequest === handledOpenRequestRef.current) return;

        handledOpenRequestRef.current = openRequest;

        if (!openRequest || isSetupPending) return;

        setTab(resolveOpenTab(planSeq, firstAccessDt));
        setMissionView("list");
        setGuideMission(null);
        setIsExpanded(true);
        persistExpanded(true);
    }, [firstAccessDt, isSetupPending, openRequest, persistExpanded, planSeq]);

    useEffect(() => {
        if (planSeq === null || planSeq === undefined || !examPlanJoined) {
            setMissionStatusData(null);
            return undefined;
        }

        let isActive = true;
        const queryParam = { MMBR_SEQ: mmbrSeq, PLAN_SEQ: planSeq };
        const param = {
            REQUEST_API: Constants.MID_STUDY_API.SEL_PLAN_MISS_STATUS,
            HTTP_METHOD: Constants.HTTP_METHOD.GET,
        };

        callPost(
            { user_id: userId },
            param,
            (res) => {
                if (isActive) {
                    setMissionStatusData(res?.RESP_RESULT?.RESP_RESULT || res?.RESP_RESULT);
                }
            },
            queryParam,
        );

        return () => {
            isActive = false;
        };
    }, [examPlanJoined, mmbrSeq, planSeq, userId, pathname]);

    /**
     * AI독서실 운영 정보(CMS 세팅값)를 직접 조회한다.
     * 미션현황 응답의 BRCS_NEXT_INFO 만으로는 오늘 운영 여부를 알 수 없어 GO 버튼이 계속 비활성이었다.
     */
    useEffect(() => {
        if (!mmbrSeq || !examPlanJoined) {
            setBrcsTodayInfo(null);
            return undefined;
        }

        let isActive = true;

        const loadBrcsInfo = () => {
            callPost(
                { user_id: userId },
                {
                    REQUEST_API: Constants.BRCS_API.BRCS_INFO,
                    HTTP_METHOD: Constants.HTTP_METHOD.GET,
                },
                (res) => {
                    if (!isActive) return;
                    const responseData = res?.RESP_RESULT?.RESP_RESULT || res?.RESP_RESULT;
                    setBrcsTodayInfo(responseData?.BRCS_INFO || null);
                },
                { MMBR_SEQ: mmbrSeq },
            );
        };

        loadBrcsInfo();
        const timer = setInterval(loadBrcsInfo, 60000);

        return () => {
            isActive = false;
            clearInterval(timer);
        };
    }, [examPlanJoined, mmbrSeq, userId]);

    /**
     * 미션 안내 탭이 실제로 화면에 열린 뒤에만 '안내를 봤음'으로 기록한다.
     * 안내 팝업 단계(isSetupPending)에서는 본문이 렌더되지 않으므로 기록하면 안 된다.
     */
    useEffect(() => {
        if (!isSetupPending && isExpanded && tab === "guide") markGuideViewed(planSeq);
    }, [isExpanded, isSetupPending, planSeq, tab]);

    const handleToggle = useCallback(() => {
        if (isSetupPending) {
            ServiceUnit_B(xAPI_ID_VALUE.serviceUnit.btn_epm_0000).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
            if (onRequestSetup) onRequestSetup();
            return;
        }

        if (isExpanded) {
            setIsExpanded(false);
            persistExpanded(false);
            return;
        }

        ServiceUnit_B(xAPI_ID_VALUE.serviceUnit.btn_epm_0000).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
        setTab(resolveOpenTab(planSeq, firstAccessDt));
        setMissionView("list");
        setGuideMission(null);
        setIsExpanded(true);
        persistExpanded(true);
    }, [ServiceUnit_B, firstAccessDt, isExpanded, isSetupPending, onRequestSetup, persistExpanded, planSeq]);

    const handleTabChange = useCallback(
        (nextTab) => {
            const xapiIdByTab = {
                mission: xAPI_ID_VALUE.serviceUnit.btn_epm_0016,
                guide: xAPI_ID_VALUE.serviceUnit.btn_epm_0011,
                score: xAPI_ID_VALUE.serviceUnit.btn_epm_0012,
                review: xAPI_ID_VALUE.serviceUnit.btn_epm_0013,
            };
            const xapiId = xapiIdByTab[nextTab];

            if (xapiId) ServiceUnit_B(xapiId).setContent(EXAM_PLAN_XAPI_CONTENT).selected();
            localStorage.setItem(getSelectedTabStorageKey(planSeq), nextTab);
            setTab(nextTab);
            setMissionView("list");
            setGuideMission(null);
        },
        [ServiceUnit_B, planSeq],
    );

    const handleFinalInfoClick = useCallback(() => {
        setGuideMission(8);
        localStorage.setItem(getSelectedTabStorageKey(planSeq), "guide");
        setTab("guide");
        setMissionView("list");
    }, [planSeq]);

    /**
     * 홈 밖(상세 페이지)에서는 접힌 토글 버튼을 구형 소형 버튼으로만 노출한다.
     * 넓은 배너형 버튼(72x200)이 화면의 기존 콘텐츠를 가린다는 기획 요건.
     */
    const panelClassName = `expln${isHome ? "" : " expln--compact-toggle"}`;

    if (isSetupPending) {
        return (
            <section className={`${panelClassName} collapsed`}>
                <button type="button" className="expln__toggle collapsed" onClick={handleToggle} />
            </section>
        );
    }

    return (
        <>
            <div className={`expln-dim ${isExpanded ? "showing" : ""}`} onClick={handleToggle} />
            <section className={`${panelClassName} ${isExpanded ? "showing" : "collapsed"}`}>
                <button
                    type="button"
                    className={`expln__toggle ${isExpanded ? "" : "collapsed"}`}
                    onClick={handleToggle}
                />
                <ExamPlanHeader
                    exam={exam}
                    planInfo={missionPlanInfo}
                    showCountdown={isExpanded}
                    completionMembers={missionStatusData?.MIS_AVAIL_MMBR_INFO}
                    mmbrSeq={mmbrSeq}
                />
                <ExamPlanTabs activeTab={tab} onChange={handleTabChange} />

                {isExpanded && (
                    <div className="expln__cont">
                        {(tab === "mission" || tab === "score") && (
                            <ExamPlanFinalCard
                                completeMissionCount={completeMissionCount}
                                totalMissionCount={totalMissionCount}
                                onInfoClick={handleFinalInfoClick}
                                gradCd={missionPlanInfo?.GRAD_CD}
                            />
                        )}

                        <div className={`expln__work ${tab === "guide" || tab === "review" ? "is-wide" : ""}`}>
                            {tab === "mission" && missionView === "list" && (
                                <ExamPlanMissionStatus
                                    missionStatusList={missionStatusData?.MIS_STATUS_LIST}
                                    missionStatusTotal={missionStatusData?.MIS_STATUS_TOTAL}
                                    todayMissionHistoryList={missionStatusData?.TODAY_MISN_HST_LIST}
                                    missionStatusData={missionStatusData}
                                    onNavigate={onMissionMove}
                                    onVisit={onMissionVisit}
                                    onScholarReview={() => handleTabChange("review")}
                                    isAiStudyRoomActive={isAiStudyRoomActive}
                                    isMissionExpired={isMissionExpired}
                                />
                            )}
                            {tab === "mission" && missionView === "final" && <ExamPlanFinalDetail exam={exam} />}
                            {tab === "score" && (
                                <Suspense fallback={null}>
                                    <ExamPlanScore
                                        schoolScoreInfo={missionStatusData?.SCHL_SCORE_INFO}
                                        onEditScore={onEditScore}
                                    />
                                </Suspense>
                            )}
                            {tab === "guide" && <ExamPlanGuide guideItems={guideItems} initialMission={guideMission} />}
                            {tab === "review" && <ExamPlanReviews reviews={EXAM_PLAN_REVIEWS} />}
                        </div>
                    </div>
                )}
            </section>
        </>
    );
}

export default React.memo(ExamPlan);
